import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

import type {
  CanonicalTreeEdgeKind,
  CanonicalTreeEdgeStatus,
  CanonicalTreeRelationshipEdge,
} from './CanonicalTreeRelationshipEdge';

export interface CanonicalTreeProfileConnectionPerson {
  readonly memberId: string;
  readonly name: string;
}

export interface CanonicalTreeProfileModernConnection {
  readonly edgeId: string;
  readonly edgeKind: CanonicalTreeEdgeKind;
  readonly status: CanonicalTreeEdgeStatus;
  readonly otherMember: CanonicalTreeProfileConnectionPerson;
  readonly sharedChildren: readonly CanonicalTreeProfileConnectionPerson[];
  readonly preferredFamilyTitle: string | null;
}

export interface CanonicalTreeProfileDirectConnectionsProjection {
  readonly subjectMemberId: string;
  readonly viewerMemberId: string | null;
  readonly parents: readonly CanonicalTreeProfileConnectionPerson[];
  readonly siblings: readonly CanonicalTreeProfileConnectionPerson[];
  readonly children: readonly CanonicalTreeProfileConnectionPerson[];
  readonly sharedChildrenWithViewer: readonly CanonicalTreeProfileConnectionPerson[];
  readonly otherRecordedChildren: readonly CanonicalTreeProfileConnectionPerson[];
  readonly currentPartners: readonly CanonicalTreeProfileConnectionPerson[];
  readonly formerPartners: readonly CanonicalTreeProfileConnectionPerson[];
  readonly modernConnections: readonly CanonicalTreeProfileModernConnection[];
  readonly hasConnections: boolean;
}

const active = (member: GraphMember): boolean =>
  member.approvalStatus !== 'rejected' &&
  String(member.profileState ?? '') !== 'deleted';

const person = (
  member: GraphMember,
): CanonicalTreeProfileConnectionPerson =>
  Object.freeze({
    memberId: member.id,
    name: member.name || 'Family member',
  });

const uniquePeople = (
  people: readonly CanonicalTreeProfileConnectionPerson[],
): readonly CanonicalTreeProfileConnectionPerson[] => {
  const map = new Map<string, CanonicalTreeProfileConnectionPerson>();
  for (const entry of people) map.set(entry.memberId, entry);
  return Object.freeze(
    [...map.values()].sort((left, right) =>
      left.name.localeCompare(right.name) ||
      left.memberId.localeCompare(right.memberId),
    ),
  );
};

const otherEndpoint = (
  edge: CanonicalTreeRelationshipEdge,
  subjectMemberId: string,
): string | null => {
  if (edge.sourceMemberId === subjectMemberId) return edge.targetMemberId;
  if (edge.targetMemberId === subjectMemberId) return edge.sourceMemberId;
  return null;
};

const isCurrentPartnership = (
  status: CanonicalTreeEdgeStatus,
): boolean => status === 'active';

/**
 * CC-TREE-LIVE-003G3WJ2
 *
 * Profile relationship facts are derived from the same canonical graph
 * used by the Tree. No display label is accepted as structural evidence.
 *
 * Co-parent multiplicity is set-based: one adult may be a parent of any
 * number of children. "Shared children with you" is exactly the
 * intersection of the subject's child set and the viewer's child set.
 */
export const projectCanonicalTreeProfileDirectConnections = (
  input: Readonly<{
    members: readonly GraphMember[];
    subjectMemberId: string;
    viewerMemberId: string | null;
    edges: readonly CanonicalTreeRelationshipEdge[];
  }>,
): CanonicalTreeProfileDirectConnectionsProjection => {
  const activeMembers = input.members.filter(active);
  const byId = new Map(activeMembers.map(member => [member.id, member] as const));
  const subject = byId.get(input.subjectMemberId) ?? null;
  const viewer = input.viewerMemberId
    ? byId.get(input.viewerMemberId) ?? null
    : null;

  if (!subject) {
    return Object.freeze({
      subjectMemberId: input.subjectMemberId,
      viewerMemberId: input.viewerMemberId,
      parents: Object.freeze([]),
      siblings: Object.freeze([]),
      children: Object.freeze([]),
      sharedChildrenWithViewer: Object.freeze([]),
      otherRecordedChildren: Object.freeze([]),
      currentPartners: Object.freeze([]),
      formerPartners: Object.freeze([]),
      modernConnections: Object.freeze([]),
      hasConnections: false,
    });
  }

  const parentIds = new Set((subject.parentIds ?? []).filter(id => byId.has(id)));
  const parents = uniquePeople(
    [...parentIds]
      .map(id => byId.get(id))
      .filter((member): member is GraphMember => Boolean(member))
      .map(person),
  );

  const childrenMembers = activeMembers.filter(member =>
    (member.parentIds ?? []).includes(subject.id),
  );
  const children = uniquePeople(childrenMembers.map(person));

  const viewerChildIds = new Set(
    viewer
      ? activeMembers
          .filter(member => (member.parentIds ?? []).includes(viewer.id))
          .map(member => member.id)
      : [],
  );

  const sharedChildrenWithViewer =
    subject.id !== viewer?.id
      ? uniquePeople(
          childrenMembers
            .filter(child => viewerChildIds.has(child.id))
            .map(person),
        )
      : Object.freeze([]);

  const sharedIds = new Set(
    sharedChildrenWithViewer.map(child => child.memberId),
  );

  const otherRecordedChildren = uniquePeople(
    childrenMembers
      .filter(child => !sharedIds.has(child.id))
      .map(person),
  );

  const siblingPeople: CanonicalTreeProfileConnectionPerson[] = [];
  if (parentIds.size > 0) {
    for (const candidate of activeMembers) {
      if (candidate.id === subject.id) continue;
      if ((candidate.parentIds ?? []).some(parentId => parentIds.has(parentId))) {
        siblingPeople.push(person(candidate));
      }
    }
  }

  const currentPartners: CanonicalTreeProfileConnectionPerson[] = [];
  const formerPartners: CanonicalTreeProfileConnectionPerson[] = [];
  const modernConnections: CanonicalTreeProfileModernConnection[] = [];

  const modernKinds = new Set<CanonicalTreeEdgeKind>([
    'step-family',
    'in-law',
    'guardian',
    'foster-family',
    'chosen-family',
    'god-family',
    'affiliated-family',
    'co-parent',
  ]);

  for (const edge of input.edges) {
    const otherId = otherEndpoint(edge, subject.id);
    if (!otherId) continue;
    const other = byId.get(otherId);
    if (!other) continue;

    if (edge.kind === 'sibling') {
      siblingPeople.push(person(other));
      continue;
    }

    if (edge.kind === 'spouse-partner') {
      if (isCurrentPartnership(edge.status)) {
        currentPartners.push(person(other));
      } else {
        formerPartners.push(person(other));
      }
      continue;
    }

    if (!modernKinds.has(edge.kind)) continue;

    const sharedChildren = uniquePeople(
      edge.sharedChildIds
        .map(childId => byId.get(childId))
        .filter((child): child is GraphMember => Boolean(child))
        .map(person),
    );

    modernConnections.push(
      Object.freeze({
        edgeId: edge.id,
        edgeKind: edge.kind,
        status: edge.status,
        otherMember: person(other),
        sharedChildren,
        preferredFamilyTitle: edge.preferredFamilyTitle ?? null,
      }),
    );
  }

  const siblings = uniquePeople(siblingPeople);
  const current = uniquePeople(currentPartners);
  const former = uniquePeople(formerPartners);
  const frozenModern = Object.freeze(
    [...modernConnections].sort((left, right) =>
      left.edgeKind.localeCompare(right.edgeKind) ||
      left.otherMember.name.localeCompare(right.otherMember.name) ||
      left.edgeId.localeCompare(right.edgeId),
    ),
  );

  const hasConnections =
    parents.length > 0 ||
    siblings.length > 0 ||
    children.length > 0 ||
    current.length > 0 ||
    former.length > 0 ||
    frozenModern.length > 0;

  return Object.freeze({
    subjectMemberId: subject.id,
    viewerMemberId: viewer?.id ?? null,
    parents,
    siblings,
    children,
    sharedChildrenWithViewer,
    otherRecordedChildren,
    currentPartners: current,
    formerPartners: former,
    modernConnections: frozenModern,
    hasConnections,
  });
};
